import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { closedAppointmentStatuses, openedAppointmentStatuses, ComplaintAppointment, Complaint, DocumentAttachment, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { SyncService, getDocumentAppointments, getSelectedComplaint } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ConfigService, core, logger } from '@icc/shared';
import { ComplaintService } from '../complaint.service';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
  selector: 'icc-complaint-appointment-dialog',
  templateUrl: './complaint-appointment-dialog.component.html',
  styleUrls: ['./complaint-appointment-dialog.component.scss']
})
export class ComplaintAppointmentDialogComponent implements OnInit, OnDestroy {
  isValidDate = false;
  isValidTime = false;

  /**
   * When true, the dialog is blocked because external changes were detected.
   */
  cannotEdit = false;

  /**
   * Reason for blocking: 'complaint' (complaint status/assignment changed) or 'appointment' (appointment status closed)
   */
  blockReason: 'complaint' | 'appointment' | null = null;

  /**
   * Set to true when external permission-affecting change was detected.
   * Once true, every store update will check if user lost permission.
   */
  private hadExternalPermissionChange = false;

  /**
   * Set to true when this appointment was modified externally.
   * Once true, every store update will check if status changed to closed.
   */
  private hadExternalAppointmentChange = false;

  /**
   * Set to true when a new appointment was added externally (add mode only).
   * Once true, every appointments store update will check for conflict.
   */
  private hadExternalAddAppointment = false;

  /**
   * Timestamp when the dialog was opened.
   */
  private dialogOpenedAt = 0;

  /**
   * Initial appointment values when dialog was opened.
   * Used to detect which fields user has modified.
   */
  private initialAppointment!: ComplaintAppointment;

  private subscriptions: Subscription[] = [];
  private activeUserCompanyId: number;

  /**
   * Cached value of whether user has manufacturer permission.
   * Loaded once on init since permissions don't change during dialog lifetime.
   */
  private hasManufacturerPermission = false;

  constructor(
    private dialogRef: DialogRef,
    @Inject(DIALOG_DATA)
    public data: {
      appointment: ComplaintAppointment;
      complaint: Complaint;
      attachments: DocumentAttachment[];
      editMode: boolean;
    },
    private store: Store<State>,
    private configService: ConfigService,
    private complaintService: ComplaintService,
    private syncService: SyncService,
    private permissionsService: NgxPermissionsService,
    private cdr: ChangeDetectorRef,
  ) {
    this.activeUserCompanyId = Number(this.configService?.user?.user?.ActiveCompany);
  }

  async ngOnInit(): Promise<void> {
    this.dialogOpenedAt = Date.now();

    // Cache manufacturer permission - doesn't change during dialog lifetime
    this.hasManufacturerPermission = await this.permissionsService.hasPermission('complaints-manage-manufacturer');

    // Store initial appointment values to detect user changes (only in edit mode)
    if (this.data.editMode && this.data.appointment) {
      this.initialAppointment = core.copy(this.data.appointment);
    }

    const permissionFields = [
      'assignedTo',
      'distributorStatusId',
      'manufacturerStatusId',
      'authorizedRetailerStatusId',
    ];

    this.subscriptions.push(
      // Subscribe to external changes - set flag when permission-affecting changes detected
      this.syncService.externalChanges$.subscribe(({
        documentIds,
        documentFields,
        appointmentIds,
        operationTypes,
        timestamp
      }) => {
        // Ignore changes that happened before dialog was opened
        if (timestamp <= this.dialogOpenedAt) {
          return;
        }

        // Check if complaint permission fields changed externally
        if (documentIds.has(this.data.complaint.uuid)) {
          if (permissionFields.some(field => documentFields.has(field))) {
            logger.log('[AppointmentDialog] Permission field changed externally');
            this.hadExternalPermissionChange = true;
            // Check immediately with current data
            if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
              logger.log('[AppointmentDialog] BLOCKING - external change removed permission');
              this.cannotEdit = true;
              this.blockReason = 'complaint';
              this.cdr.detectChanges();
            }
          }
        }

        // In edit mode: check if this appointment was modified externally
        if (this.data.editMode && this.data.appointment.uuid && appointmentIds.has(this.data.appointment.uuid)) {
          logger.log('[AppointmentDialog] Appointment modified externally');
          this.hadExternalAppointmentChange = true;
          // Check immediately with current data
          if (closedAppointmentStatuses.some((status) => status.value === this.data.appointment.status)) {
            logger.log('[AppointmentDialog] BLOCKING - appointment status changed to closed');
            this.cannotEdit = true;
            this.blockReason = 'appointment';
            this.cdr.detectChanges();
          }
          return;
        }

        // In add mode: check if a new appointment was added externally
        if (!this.data.editMode && operationTypes.has('addAppointment')) {
          logger.log('[AppointmentDialog] New appointment added externally in add mode');
          this.hadExternalAddAppointment = true;
          // Note: Can't check immediately here because we need fresh appointments list from store
        }
      }),

      // Subscribe to complaint document changes - update data and check blocking if needed
      this.store.select(getSelectedComplaint).subscribe((complaint) => {
        if (complaint && complaint.uuid === this.data.complaint.uuid) {
          Object.assign(this.data.complaint, complaint);

          // If external permission change was detected, check if user lost permission
          if (this.hadExternalPermissionChange && !this.cannotEdit) {
            if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
              logger.log('[AppointmentDialog] BLOCKING - external change removed permission');
              this.cannotEdit = true;
              this.blockReason = 'complaint';
              this.cdr.detectChanges();
            }
          }
        }
      }),

      // Subscribe to appointments - update data and verify blocking conditions
      this.store.select(getDocumentAppointments(this.data.complaint.uuid)).subscribe((appointments) => {
        if (this.data.editMode) {
          // Edit mode: update appointment data if not blocked
          if (this.data.appointment.uuid && !this.cannotEdit) {
            const updatedAppointment = appointments.find(
              (app) => app.uuid === this.data.appointment.uuid
            );
            if (updatedAppointment) {
              // If external appointment change was detected, check if status is closed
              if (this.hadExternalAppointmentChange && !this.cannotEdit) {
                if (closedAppointmentStatuses.some((status) => status.value === updatedAppointment.status)) {
                  logger.log('[AppointmentDialog] BLOCKING - appointment status changed to closed');
                  this.cannotEdit = true;
                  this.blockReason = 'appointment';
                  this.cdr.detectChanges();
                  return;
                }
              }

              // Update appointment fields that haven't been modified by user
              this.updateUnchangedFields(updatedAppointment);
            }
          }
        } else {
          // Add mode: check if another appointment is now "in progress" for this company
          // Check if external add was detected
          if (this.hadExternalAddAppointment && !this.cannotEdit) {
            const hasAppointmentInProgress = appointments.some(
              (app) => openedAppointmentStatuses.some(status => status.value === app.status)
                && Number(app.assignedCompanyId) === this.activeUserCompanyId
            );
            if (hasAppointmentInProgress) {
              logger.log('[AppointmentDialog] BLOCKING - another appointment in progress detected');
              this.cannotEdit = true;
              this.blockReason = 'appointment';
              this.cdr.detectChanges();
            }
          }
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /**
   * Updates appointment fields that haven't been modified by the user.
   * Compares current value with initial value to detect user changes.
   */
  private updateUnchangedFields(updatedAppointment: ComplaintAppointment): void {
    if (!this.initialAppointment) {
      return;
    }

    const fieldsToCheck: (keyof ComplaintAppointment)[] = [
      'date',
      'time',
      'scheduledTime',
      'timeUsed',
      'status',
      'assignedUserId',
      'comment',
    ];

    for (const field of fieldsToCheck) {
      // Check if current value equals initial value (user hasn't changed it)
      const currentValue = this.data.appointment[field];
      const initialValue = this.initialAppointment[field];

      if (JSON.stringify(currentValue) === JSON.stringify(initialValue)) {
        // Field not modified by user, safe to update from sync
        (this.data.appointment as any)[field] = updatedAppointment[field];
      }
    }
  }

  /**
   * Checks if the current status allows editing.
   */
  private currentStatusAllowEdit(): boolean {
    const userType = this.complaintService.getUserType();
    const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);

    if (statusId === 0 || isNaN(statusId)) {
      return true;
    }

    const status = this.complaintService.statuses.find((s) => s.id === statusId);
    return status?.allowEdit ?? false;
  }

  /**
   * Checks if the current user is the assigned party or has manufacturer permission.
   * Users with 'complaints-manage-manufacturer' permission bypass the assignment check.
   */
  private isUserAssigned(): boolean {
    if (this.hasManufacturerPermission) {
      return true;
    }
    const userType = this.complaintService.getUserType();
    return this.data.complaint.assignedTo?.startsWith(userType) ?? false;
  }

  validateDate(event: boolean) {
    this.isValidDate = event;
  }

  validateTime(event: boolean) {
    this.isValidTime = event;
  }

  async save() {
    this.dialogRef.close({
      appointment: this.data.appointment,
      attachments: this.data.attachments,
    });
  }

  close() {
    this.dialogRef.close();
  }
}
